import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { getStatusLabel, getStatusChipStyles } from '../../../utils/jobStatuses';
import { getPriorityLabel } from '../../../utils/jobPriorities';
import { formatJobLogEntry, formatShortDateTime } from '../../../utils/jobLogFormatters';

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseJsonObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return {};
};

const FieldRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" sx={{ minWidth: 140, color: 'text.secondary', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const SectionList = ({ title, items, renderItem, emptyText = 'None' }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
      {title} ({items.length})
    </Typography>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
    ) : (
      items.map((item, index) => (
        <Box
          key={item.id || `${title}-${index}`}
          sx={{
            mb: 1.5,
            p: 2,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        >
          {renderItem(item, index)}
        </Box>
      ))
    )}
  </Box>
);

export default function DeletedJobDetailsDialog({ selectedDeletedJob, setSelectedDeletedJob }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [tabValue, setTabValue] = useState(0);

  const record = selectedDeletedJob;
  const job = useMemo(() => parseJsonObject(record?.job), [record]);
  const technicians = useMemo(() => parseJsonArray(record?.job_technicians), [record]);
  const parts = useMemo(() => parseJsonArray(record?.job_parts), [record]);
  const proposals = useMemo(() => parseJsonArray(record?.job_proposals), [record]);
  const links = useMemo(() => parseJsonArray(record?.job_links), [record]);
  const tags = useMemo(() => parseJsonArray(record?.job_tag_assignments), [record]);
  const comments = useMemo(() => parseJsonArray(record?.comments), [record]);
  const logs = useMemo(() => parseJsonArray(record?.jobs_logs), [record]);
  const attachments = useMemo(() => parseJsonArray(record?.attachments), [record]);

  const handleClose = () => {
    setSelectedDeletedJob(null);
    setTabValue(0);
  };

  if (!record) return null;

  const { bgColor, textColor, borderColor } = getStatusChipStyles(Number(job.status));
  const statusStyles = {
    backgroundColor: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  return (
    <Dialog
      open={Boolean(record)}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          backgroundColor: 'background.paper',
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, pr: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Deleted Work Order
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {job.work_order || 'No work order'} · {job.client_name || 'Unknown client'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            <Chip
              label={getStatusLabel(Number(job.status))}
              size="small"
              sx={statusStyles}
            />
            <Chip label={getPriorityLabel(Number(job.priority))} size="small" variant="outlined" />
            {tags.map((tag) => (
              <Chip key={tag.tag_id || tag.id} label={tag.name} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close deleted job details">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 0 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          This work order was deleted on {formatShortDateTime(record.deleted_at)}
          {record.deleted_by_email ? ` by ${record.deleted_by_email}` : ''}. Attachment files were removed from storage; metadata is preserved below.
        </Alert>

        <Tabs
          value={tabValue}
          onChange={(_event, value) => setTabValue(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Summary" />
          <Tab label={`Technicians (${technicians.length})`} />
          <Tab label={`Parts (${parts.length})`} />
          <Tab label={`Proposals (${proposals.length})`} />
          <Tab label={`Links (${links.length})`} />
          <Tab label={`Comments (${comments.length})`} />
          <Tab label={`Activity (${logs.length})`} />
          <Tab label={`Attachments (${attachments.length})`} />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <FieldRow label="Original job ID" value={record.job_id} />
            <FieldRow label="Work order" value={job.work_order} />
            <FieldRow label="Client" value={job.client_name} />
            <FieldRow label="Admin" value={job.admin} />
            <FieldRow label="Dispatcher" value={job.dispatcher} />
            <FieldRow label="Trade" value={job.trade} />
            <FieldRow label="Store" value={job.store_name} />
            <FieldRow label="Address" value={[job.address, job.address_2, job.city, job.state, job.zip_code].filter(Boolean).join(', ')} />
            <FieldRow label="Description" value={job.description} />
            <FieldRow label="Notes" value={job.notes} />
            <FieldRow label="NTE" value={job.nte != null ? `$${Number(job.nte).toFixed(2)}` : null} />
            <FieldRow label="Created" value={formatShortDateTime(job.created_at)} />
            <FieldRow label="Updated" value={formatShortDateTime(job.updated_at)} />
            <FieldRow label="Deleted by" value={record.deleted_by_email || '—'} />
          </Box>
        )}

        {tabValue === 1 && (
          <SectionList
            title="Technicians"
            items={technicians}
            renderItem={(tech) => (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {tech.type === 'vendor'
                    ? tech.company_name
                    : `${tech.first_name || ''} ${tech.last_name || ''}`.trim() || tech.email}
                </Typography>
                <FieldRow label="Status" value={tech.status} />
                <FieldRow label="Tech cost" value={tech.tech_cost != null ? `$${Number(tech.tech_cost).toFixed(2)}` : null} />
                <FieldRow label="Scheduled" value={formatShortDateTime(tech.scheduled_date)} />
                <FieldRow label="Paid" value={formatShortDateTime(tech.paid_date)} />
                <FieldRow label="Notes" value={tech.notes} />
              </>
            )}
          />
        )}

        {tabValue === 2 && (
          <SectionList
            title="Parts"
            items={parts}
            renderItem={(part) => (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{part.name}</Typography>
                <FieldRow label="Cost" value={part.cost != null ? `$${Number(part.cost).toFixed(2)}` : null} />
                <FieldRow label="Description" value={part.description} />
                <FieldRow label="URL" value={part.url} />
                <FieldRow label="Approved" value={part.is_approved ? 'Yes' : 'No'} />
                <FieldRow label="Created by" value={part.created_by} />
              </>
            )}
          />
        )}

        {tabValue === 3 && (
          <SectionList
            title="Proposals"
            items={proposals}
            renderItem={(proposal) => (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  ${Number(proposal.price || 0).toFixed(2)}
                  {proposal.invoice_number ? ` · Invoice ${proposal.invoice_number}` : ''}
                </Typography>
                <FieldRow label="Text" value={proposal.proposal_text} />
                <FieldRow label="Approved" value={proposal.is_approved ? 'Yes' : 'No'} />
                <FieldRow label="Invoiced" value={proposal.invoiced ? 'Yes' : 'No'} />
                <FieldRow label="Created by" value={proposal.created_by} />
                {(proposal.items || []).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Items</Typography>
                    {(proposal.items || []).map((item) => (
                      <Typography key={item.id} variant="body2" color="text.secondary">
                        {item.type}: {item.quantity} × ${Number(item.unit_price || 0).toFixed(2)} = ${Number(item.price || 0).toFixed(2)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            )}
          />
        )}

        {tabValue === 4 && (
          <SectionList
            title="Links"
            items={links}
            renderItem={(link) => (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{link.label}</Typography>
                <FieldRow label="URL" value={link.url} />
              </>
            )}
          />
        )}

        {tabValue === 5 && (
          <SectionList
            title="Comments"
            items={comments}
            renderItem={(comment) => (
              <>
                <FieldRow label="Posted" value={formatShortDateTime(comment.created_at)} />
                <FieldRow label="Comment" value={comment.comment_text} />
              </>
            )}
          />
        )}

        {tabValue === 6 && (
          <SectionList
            title="Activity"
            items={logs}
            renderItem={(log) => (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {formatJobLogEntry(log)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatShortDateTime(log.created_at)}
                  {log.actor_email ? ` · ${log.actor_email}` : ''}
                </Typography>
              </>
            )}
          />
        )}

        {tabValue === 7 && (
          <SectionList
            title="Attachments"
            items={attachments}
            renderItem={(attachment) => (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{attachment.file_name}</Typography>
                <FieldRow label="Type" value={attachment.entity_type} />
                <FieldRow label="Category" value={attachment.category} />
                <FieldRow label="Size" value={attachment.file_size != null ? `${attachment.file_size} bytes` : null} />
                <FieldRow label="Private" value={attachment.is_private ? 'Yes' : 'No'} />
                <FieldRow label="Uploaded" value={formatShortDateTime(attachment.created_at)} />
              </>
            )}
            emptyText="No attachment metadata was archived"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
